import React, { useState, useEffect, useCallback } from 'react';
import { getScreenerResults } from '../services/screener';
import { getExchanges } from '../services/exchange';
import { loadSectorIndustries, getUniqueSectors, getIndustriesForSector, SectorIndustry } from '../services/sectorIndustry';
import { ScreenerFilters, ScreenerResult, ScreenerFilter, ScreenerResponse } from '../types/screener';
import { Exchange } from '../types/exchange';
import Button from './Button';

const Screener: React.FC = () => {
  const [results, setResults] = useState<ScreenerResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [sectorIndustries, setSectorIndustries] = useState<SectorIndustry[]>([]);
  const [sectors, setSectors] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  
  const [filters, setFilters] = useState<ScreenerFilters>({
    filters: [{
      field: 'exchange',
      operation: '=',
      value: 'US' // Default to US exchange
    }],
    limit: 50,
    offset: 0,
    sort: 'market_capitalization.desc'
  });

  const [formFilters, setFormFilters] = useState({
    type: '',
    marketCapMin: '',
    marketCapMax: '',
    peRatioMin: '',
    peRatioMax: '',
    exchange: 'US', // Default to US exchange
    sector: '',
    industry: ''
  });

  const loadFilterData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load exchanges
      const exchangeList = await getExchanges();
      setExchanges(exchangeList);

      // Load sectors and industries
      const sectorData = await loadSectorIndustries();
      setSectorIndustries(sectorData);
      setSectors(getUniqueSectors(sectorData));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load filter data';
      setError(errorMessage);
      console.error('Error loading filter data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response: ScreenerResponse = await getScreenerResults(filters);
      setResults(response.data);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch results';
      setError(errorMessage);
      console.error('Error fetching screener results:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadFilterData();
    fetchResults();
  }, [fetchResults]);

  useEffect(() => {
    if (formFilters.sector) {
      setIndustries(getIndustriesForSector(sectorIndustries, formFilters.sector));
    } else {
      setIndustries([]);
    }
  }, [formFilters.sector, sectorIndustries]);

  const handleFormFilterChange = (key: keyof typeof formFilters, value: string) => {
    setFormFilters(prev => {
      // Reset industry when sector changes
      if (key === 'sector') {
        return {
          ...prev,
          [key]: value,
          industry: ''
        };
      }
      return {
        ...prev,
        [key]: value
      };
    });
  };

  const handleSymbolClick = (code: string) => {
    // Handle symbol click - can be implemented later
    console.log('Symbol clicked:', code);
  };

  const applyFilters = () => {
    const newFilters: ScreenerFilter[] = [];

    if (formFilters.type) {
      newFilters.push({
        field: 'type',
        operation: '=',
        value: formFilters.type
      });
    }

    if (formFilters.exchange) {
      newFilters.push({
        field: 'exchange',
        operation: '=',
        value: formFilters.exchange
      });
    }

    if (formFilters.sector) {
      newFilters.push({
        field: 'sector',
        operation: 'match',
        value: formFilters.sector
      });
    }

    if (formFilters.industry) {
      newFilters.push({
        field: 'industry',
        operation: 'match',
        value: formFilters.industry
      });
    }

    if (formFilters.marketCapMin) {
      newFilters.push({
        field: 'market_capitalization',
        operation: '>=',
        value: Number(formFilters.marketCapMin) * 1e6 // Convert to actual value from millions
      });
    }

    if (formFilters.marketCapMax) {
      newFilters.push({
        field: 'market_capitalization',
        operation: '<=',
        value: Number(formFilters.marketCapMax) * 1e6
      });
    }

    if (formFilters.peRatioMin) {
      newFilters.push({
        field: 'pe_ratio',
        operation: '>=',
        value: Number(formFilters.peRatioMin)
      });
    }

    if (formFilters.peRatioMax) {
      newFilters.push({
        field: 'pe_ratio',
        operation: '<=',
        value: Number(formFilters.peRatioMax)
      });
    }

    setFilters(prev => ({
      ...prev,
      filters: newFilters
    }));
  };

  const formatMarketCap = (value: number): string => {
    if (value >= 1e12) {
      return `$${(value / 1e12).toFixed(2)}T`;
    }
    if (value >= 1e9) {
      return `$${(value / 1e9).toFixed(2)}B`;
    }
    if (value >= 1e6) {
      return `$${(value / 1e6).toFixed(2)}M`;
    }
    return `$${value.toFixed(2)}`;
  };

  // Show loading state
  if (loading) {
    return (
      <div style={{ backgroundColor: '#f0f0f0', minHeight: '100vh', padding: '2rem' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div className="text-center py-8">Loading...</div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{ backgroundColor: '#f0f0f0', minHeight: '100vh', padding: '2rem' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div className="text-center py-8 text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f0f0f0', minHeight: '100vh', padding: '2rem' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div className="grid grid-cols-12 gap-6">
          {/* Filters Section */}
          <div className="col-span-3">
            <div className="bg-white rounded-lg shadow p-6 sticky top-4">
              <h3 className="text-xl font-semibold mb-4">Filters</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">Type</label>
                  <select
                    className="w-full p-2 border border-gray-200 rounded bg-white"
                    value={formFilters.type}
                    onChange={(e) => handleFormFilterChange('type', e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="Stock">Stocks</option>
                    <option value="ETF">ETFs</option>
                    <option value="Fund">Funds</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-2">Exchange</label>
                  <select
                    className="w-full p-2 border border-gray-200 rounded bg-white"
                    value={formFilters.exchange}
                    onChange={(e) => handleFormFilterChange('exchange', e.target.value)}
                  >
                    <option value="">All Exchanges</option>
                    {exchanges.map(exchange => (
                      <option key={exchange.code} value={exchange.code}>
                        {exchange.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-2">Sector</label>
                  <select
                    className="w-full p-2 border border-gray-200 rounded bg-white"
                    value={formFilters.sector}
                    onChange={(e) => handleFormFilterChange('sector', e.target.value)}
                  >
                    <option value="">All Sectors</option>
                    {sectors.map(sector => (
                      <option key={sector} value={sector}>
                        {sector}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-2">Industry</label>
                  <select
                    className="w-full p-2 border border-gray-200 rounded bg-white"
                    value={formFilters.industry}
                    onChange={(e) => handleFormFilterChange('industry', e.target.value)}
                    disabled={!formFilters.sector}
                  >
                    <option value="">All Industries</option>
                    {industries.map(industry => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-2">Market Cap Range (USD Millions)</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      className="w-full p-2 border border-gray-200 rounded bg-white"
                      value={formFilters.marketCapMin}
                      onChange={(e) => handleFormFilterChange('marketCapMin', e.target.value)}
                      placeholder="Min"
                    />
                    <input
                      type="number"
                      className="w-full p-2 border border-gray-200 rounded bg-white"
                      value={formFilters.marketCapMax}
                      onChange={(e) => handleFormFilterChange('marketCapMax', e.target.value)}
                      placeholder="Max"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-2">P/E Ratio Range</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      className="w-full p-2 border border-gray-200 rounded bg-white"
                      value={formFilters.peRatioMin}
                      onChange={(e) => handleFormFilterChange('peRatioMin', e.target.value)}
                      placeholder="Min"
                    />
                    <input
                      type="number"
                      className="w-full p-2 border border-gray-200 rounded bg-white"
                      value={formFilters.peRatioMax}
                      onChange={(e) => handleFormFilterChange('peRatioMax', e.target.value)}
                      placeholder="Max"
                    />
                  </div>
                </div>

                <Button onClick={applyFilters} fullWidth>
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="col-span-9">
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold mb-1">Stock Screener</h2>
                    <div className="text-gray-600">
                      {filters.filters.length === 0 ? (
                        '1 active filter'
                      ) : (
                        `${filters.filters.length} active filter${filters.filters.length > 1 ? 's' : ''}`
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button>
                      <span className="material-icons text-lg mr-1">settings</span>
                      Columns
                    </Button>
                  </div>
                </div>
              </div>

              {/* Results Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Symbol ↑</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sector</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Industry</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Market Cap</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Dividend Yield</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {results.map((result) => (
                      <tr key={`${result.code}-${result.exchange}`}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button 
                            onClick={() => handleSymbolClick(result.code)}
                            className="text-blue-600 hover:underline text-left"
                          >
                            {result.code}
                          </button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">{result.name}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{result.sector}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{result.industry}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          {result.market_capitalization ? formatMarketCap(result.market_capitalization) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          {result.dividend_yield ? `${(result.dividend_yield * 100).toFixed(2)}%` : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Screener;
